import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTenantTheme } from '../api/hooks'
import type { TenantTheme } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useBranding } from '../brand/BrandingProvider'
import { applyTheme, themeFingerprint } from './applyTheme'

/*
  Applies the active organization's theme to the document (Tracer's ThemeProvider). The theme
  comes from GET /organizations/{orgId}/theme; the logos are fetched once and shown as object
  URLs (ADR 0020: no offline copy). On ranch switch the provider reverts the previous
  organization's tokens in full and applies the next.

  Identity is taken from the serialized document, not from the object: React Query hands
  back a fresh object on every refetch, and keyed on the object every refetch would rip
  every custom property off the document and write it back.
*/

export interface ResolvedBrand {
  /** The platform's name, from the branding record. Never themed. */
  productName: string
  /** The ranch's display name for the header, sidebar and reports. */
  ranchName: string
  /** A ranch mark for light backgrounds, as an object URL, or null for the platform mark. */
  logoLightUrl: string | null
  /** A ranch mark for the dark sidebar, or null. */
  logoDarkUrl: string | null
  /** True when a ranch theme is active, which is when the sidebar footer shows the platform attribution. */
  themed: boolean
  theme: TenantTheme | null
}

const BrandContext = createContext<ResolvedBrand | null>(null)

/** Fetches a logo and returns an object URL, or null when it cannot be had. */
async function logoUrl(url: string | null): Promise<string | null> {
  if (!url) return null
  try {
    const response = await fetch(url, { mode: 'cors' })
    if (!response.ok) return null
    return URL.createObjectURL(await response.blob())
  } catch {
    return null
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { scope, activeMembership } = useAuth()
  const branding = useBranding()
  const organizationId = scope?.organizationId ?? null
  const query = useTenantTheme(organizationId, true)
  const [logos, setLogos] = useState<{ key: string; light: string | null; dark: string | null }>({
    key: '',
    light: null,
    dark: null,
  })

  const resolved: TenantTheme | null = organizationId ? (query.data ?? null) : null

  const fingerprint = themeFingerprint(resolved)
  const theme = useMemo(() => resolved, [fingerprint]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    let cancelled = false
    const key = `${theme?.logoLightUrl ?? ''}|${theme?.logoDarkUrl ?? ''}`
    void Promise.all([
      logoUrl(theme?.logoLightUrl ?? null),
      logoUrl(theme?.logoDarkUrl ?? null),
    ]).then(([light, dark]) => {
      if (!cancelled) setLogos({ key, light, dark })
    })
    return () => {
      cancelled = true
    }
  }, [theme])

  const brand = useMemo<ResolvedBrand>(
    () => ({
      productName: branding.productName,
      ranchName:
        theme?.ranchDisplayName?.trim() ||
        activeMembership?.ranch?.name ||
        activeMembership?.organization.name ||
        '',
      logoLightUrl: logos.light,
      logoDarkUrl: logos.dark,
      themed: theme !== null,
      theme,
    }),
    [branding.productName, theme, activeMembership, logos],
  )

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
}

/** Product and ranch names and logo URLs as the active organization sees them. */
// eslint-disable-next-line react-refresh/only-export-components
export function useBrand(): ResolvedBrand {
  const brand = useContext(BrandContext)
  if (brand === null) {
    throw new Error('useBrand must be used inside ThemeProvider.')
  }
  return brand
}
